import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Heart, Utensils, Shirt, GraduationCap,
  Users, ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';

const programs = [
  {
    id: 'orphan-support',
    icon: Heart,
    title: 'Orphan Support Fund',
    description: 'Comprehensive care for orphans covering all essential needs.',
    color: 'primary',
    whatWeDo: [
      'Provide daily meals and nutritious food packages',
      'Supply clothing for all seasons including winter gear',
      'Cover medical expenses and prescriptions',
      'Fund education from primary to higher studies',
      'Assist with housing and shelter needs'
    ],
    whoItHelps: 'Orphaned children in our community who have lost one or both parents.',
    howFundsUsed: 'Direct support to orphan families - food, clothing, medicine, education, shelter.',
    howToApply: 'Contact us confidentially through our Support Request form or email.',
    howToSupport: 'Donate to the Orphan Support Fund or sponsor an orphan monthly.'
  },
  {
    id: 'food-bank',
    icon: Utensils,
    title: 'Food Bank & Grocery Support',
    description: 'Food distribution to families facing food insecurity.',
    color: 'secondary',
    whatWeDo: [
      'Distribute food hampers to registered families',
      'Provide fresh produce and essential groceries',
      'Offer culturally appropriate halal food items',
      'Support during Ramadan with special food packages'
    ],
    whoItHelps: 'Low-income families and individuals struggling with food insecurity in Barrie and surrounding areas.',
    howFundsUsed: 'Purchase of groceries, storage, and distribution logistics.',
    howToApply: 'Fill out the Support Request form indicating food assistance needed.',
    howToSupport: 'Donate funds, volunteer at distribution events, or donate non-perishable items.'
  },
  {
    id: 'clothing',
    icon: Shirt,
    title: 'Clothing & Winter Essentials',
    description: 'Essential clothing and winter gear for vulnerable families.',
    color: 'primary',
    whatWeDo: [
      'Collect and distribute gently used clothing',
      'Provide new winter coats, boots, and accessories',
      'Supply school uniforms and backpacks',
      'Distribute winter kits including blankets and warm clothing'
    ],
    whoItHelps: 'Families and individuals who cannot afford adequate clothing, especially winter essentials.',
    howFundsUsed: 'Purchase of new winter items and distribution costs.',
    howToApply: 'Submit a Support Request specifying clothing sizes and needs.',
    howToSupport: 'Donate clothing in good condition, fund winter kit purchases, or volunteer at drives.'
  },
  {
    id: 'education',
    icon: GraduationCap,
    title: 'Education Support',
    description: 'Tutoring, school supplies, and Islamic education.',
    color: 'secondary',
    whatWeDo: [
      'Provide tutoring in core subjects',
      'Supply school materials and supplies',
      'Offer Quran and Islamic studies classes',
      'Support post-secondary education costs'
    ],
    whoItHelps: 'Students from low-income families and those seeking Islamic education.',
    howFundsUsed: 'Tutor payments, educational materials, and program costs.',
    howToApply: 'Register through our Support Request form.',
    howToSupport: 'Volunteer as a tutor, donate supplies, or fund education programs.'
  }
];

const ProgramsPage = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              True North Muslim Community
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Our <span className="text-gradient">Charitable Programs</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our programs are designed to support orphans and vulnerable families in our community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-24 lg:py-32" data-testid="programs-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {programs.map((program, index) => (
            <motion.div
              key={program.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              id={program.id}
              className="scroll-mt-24"
              data-testid={`program-${program.id}`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className={`p-4 rounded-2xl ${program.color === 'primary' ? 'bg-primary/10' : 'bg-secondary/10'}`}>
                      <program.icon className={`h-8 w-8 ${program.color === 'primary' ? 'text-primary' : 'text-secondary'}`} />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-foreground">{program.title}</h2>
                  </div>
                  <p className="text-muted-foreground text-lg leading-relaxed">{program.description}</p>
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-foreground font-['Outfit']">What We Do</h4>
                    <ul className="space-y-2">
                      {program.whatWeDo.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-muted-foreground">
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
                    <div>
                      <h4 className="font-semibold text-foreground font-['Outfit'] mb-2">Who It Helps</h4>
                      <p className="text-sm text-muted-foreground">{program.whoItHelps}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground font-['Outfit'] mb-2">How Funds Are Used</h4>
                      <p className="text-sm text-muted-foreground">{program.howFundsUsed}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground font-['Outfit'] mb-2">How to Apply</h4>
                      <p className="text-sm text-muted-foreground">{program.howToApply}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground font-['Outfit'] mb-2">How to Support</h4>
                      <p className="text-sm text-muted-foreground">{program.howToSupport}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Link to="/donate" data-testid={`donate-${program.id}`}>
                      <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                        <Heart className="mr-2 h-4 w-4" />
                        Support This Program
                      </Button>
                    </Link>
                    <Link to="/support" data-testid={`apply-${program.id}`}>
                      <Button variant="outline" className="rounded-full">
                        Request Support
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="programs-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            title="Ready to Make a Difference?"
            description="Your support helps us serve more families in need."
          />
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate">
              <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </Link>
            <Link to="/volunteer">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                <Users className="mr-2 h-5 w-5" />
                Volunteer
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
